import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  VideoCameraIcon,
  SpeakerWaveIcon,
  PhotoIcon,
  MagnifyingGlassIcon,
  ShareIcon,
  FolderIcon,
  XMarkIcon,
  ArrowTopRightOnSquareIcon,
} from '@heroicons/react/24/outline';
import { toast } from 'react-hot-toast';
import { projectMediaApi, ProjectMediaItem, ProjectMediaType } from '@/services/projectMediaApi';
import { projectApi, Project } from '@/services/api';
import Pagination from '@/components/Common/Pagination';

const tabs: { id: ProjectMediaType; label: string; icon: React.ElementType }[] = [
  { id: 'videos', label: 'Videos', icon: VideoCameraIcon },
  { id: 'audio', label: 'Audio Files', icon: SpeakerWaveIcon },
  { id: 'images', label: 'Images', icon: PhotoIcon },
];

const ITEMS_PER_PAGE = 12;

const ProjectFilesPage: React.FC = () => {
  const { type = 'videos' } = useParams<{ type: ProjectMediaType }>();
  const navigate = useNavigate();
  const activeTab = tabs.find((t) => t.id === type) || tabs[0];

  const [items, setItems] = useState<ProjectMediaItem[]>([]);
  const [totalItems, setTotalItems] = useState(0);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [page, setPage] = useState(1);
  const [shareItem, setShareItem] = useState<ProjectMediaItem | null>(null);
  const [projects, setProjects] = useState<Project[]>([]);
  const [selectedProjectId, setSelectedProjectId] = useState('');
  const [sharing, setSharing] = useState(false);

  useEffect(() => {
    if (!tabs.some((t) => t.id === type)) {
      navigate('/app/projects/files/videos', { replace: true });
    }
  }, [type, navigate]);

  useEffect(() => {
    const t = setTimeout(() => setDebouncedSearch(search.trim()), 300);
    return () => clearTimeout(t);
  }, [search]);

  useEffect(() => {
    setPage(1);
  }, [activeTab.id, debouncedSearch]);

  useEffect(() => {
    loadMedia();
  }, [activeTab.id, debouncedSearch, page]);

  const loadMedia = async () => {
    setLoading(true);
    try {
      const res = await projectMediaApi.getMedia(activeTab.id, {
        search: debouncedSearch || undefined,
        limit: ITEMS_PER_PAGE,
        offset: (page - 1) * ITEMS_PER_PAGE,
      });
      if (res.success) {
        setItems(res.data.items);
        setTotalItems(res.data.pagination.total);
      } else {
        toast.error('Failed to load media');
      }
    } catch (err: any) {
      toast.error(err?.response?.data?.message || 'Failed to load media');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (shareItem) {
      projectApi
        .getAll({ limit: 100 })
        .then((res) => {
          if (res.data.success) {
            setProjects(res.data.data.projects || []);
          }
        })
        .catch(() => toast.error('Failed to load projects'));
    }
  }, [shareItem]);

  const handleShare = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!shareItem || !selectedProjectId) return;
    setSharing(true);
    try {
      await projectMediaApi.shareMedia({
        itemId: shareItem._id,
        targetProjectId: selectedProjectId,
        itemType: shareItem.type,
        name: shareItem.name,
      });
      toast.success(`Shared "${shareItem.name}" to project`);
      setShareItem(null);
      setSelectedProjectId('');
    } catch (err: any) {
      toast.error(err?.response?.data?.message || 'Failed to share media');
    } finally {
      setSharing(false);
    }
  };

  const renderMediaPreview = (item: ProjectMediaItem) => {
    if (item.type === 'video' && item.url) {
      return (
        <video
          src={item.url}
          className="h-20 w-32 rounded object-cover bg-gray-100"
          preload="metadata"
          muted
          onMouseEnter={(e) => e.currentTarget.play().catch(() => {})}
          onMouseLeave={(e) => {
            e.currentTarget.pause();
            e.currentTarget.currentTime = 0;
          }}
        />
      );
    }
    if (item.type === 'audio' && item.url) {
      return (
        <div className="h-20 w-32 rounded bg-gray-100 dark:bg-gray-700 flex items-center justify-center">
          <SpeakerWaveIcon className="h-8 w-8 text-gray-400" />
        </div>
      );
    }
    if (item.type === 'image' && item.url) {
      return (
        <img
          src={item.url}
          alt={item.name}
          className="h-20 w-32 rounded object-cover bg-gray-100"
        />
      );
    }
    const Icon = item.type === 'video' ? VideoCameraIcon : item.type === 'audio' ? SpeakerWaveIcon : PhotoIcon;
    return (
      <div className="h-20 w-32 rounded bg-gray-100 dark:bg-gray-700 flex items-center justify-center">
        <Icon className="h-8 w-8 text-gray-400" />
      </div>
    );
  };

  return (
    <div className="p-6 max-w-7xl mx-auto">
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-gray-900 dark:text-white">Project Files</h1>
        <p className="text-sm text-gray-500 dark:text-gray-400">
          Search, preview and share your videos, audio and images across projects.
        </p>
      </div>

      {/* Tabs */}
      <div className="border-b border-gray-200 dark:border-gray-700 mb-6">
        <nav className="-mb-px flex space-x-8">
          {tabs.map((tab) => {
            const Icon = tab.icon;
            const active = tab.id === activeTab.id;
            return (
              <button
                key={tab.id}
                onClick={() => navigate(`/app/projects/files/${tab.id}`)}
                className={`
                  flex items-center gap-2 py-4 px-1 border-b-2 text-sm font-medium
                  ${active ? 'border-blue-500 text-blue-600 dark:text-blue-400' : 'border-transparent text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300 hover:border-gray-300 dark:hover:border-gray-600'}
                `}
              >
                <Icon className="h-5 w-5" />
                {tab.label}
              </button>
            );
          })}
        </nav>
      </div>

      {/* Search */}
      <div className="relative mb-6 max-w-md">
        <MagnifyingGlassIcon className="h-5 w-5 absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
        <input
          type="text"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder={`Search ${activeTab.label.toLowerCase()}...`}
          className="pl-10 pr-4 py-2 border border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-white rounded-md focus:ring-blue-500 focus:border-blue-500 w-full"
        />
      </div>

      {/* Images placeholder */}
      {activeTab.id === 'images' && (
        <div className="text-center py-16 bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700">
          <PhotoIcon className="h-12 w-12 text-gray-400 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-gray-900 dark:text-white">Image library coming soon</h3>
          <p className="text-gray-500 dark:text-gray-400 mt-1">Image uploads and management will be available here.</p>
        </div>
      )}

      {/* Loading */}
      {loading && activeTab.id !== 'images' && (
        <div className="py-12 text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto"></div>
        </div>
      )}

      {/* Empty state */}
      {!loading && activeTab.id !== 'images' && items.length === 0 && (
        <div className="text-center py-16 bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700">
          <FolderIcon className="h-12 w-12 text-gray-400 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-gray-900 dark:text-white">No {activeTab.label.toLowerCase()} found</h3>
          <p className="text-gray-500 dark:text-gray-400 mt-1">
            {search ? 'Try a different search term.' : 'Create some media from the Audio or Video sections.'}
          </p>
        </div>
      )}

      {/* Grid */}
      {!loading && activeTab.id !== 'images' && items.length > 0 && (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {items.map((item) => (
            <div
              key={item._id}
              className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 overflow-hidden hover:shadow-md transition-shadow"
            >
              <div className="p-4 flex gap-4">
                {renderMediaPreview(item)}
                <div className="flex-1 min-w-0">
                  <h4 className="text-sm font-semibold text-gray-900 dark:text-white truncate" title={item.name}>
                    {item.name}
                  </h4>
                  <p className="text-xs text-gray-500 dark:text-gray-400 mt-1 capitalize">{item.status}</p>
                  {item.projectName && (
                    <p className="text-xs text-gray-500 dark:text-gray-400 mt-1 truncate">Project: {item.projectName}</p>
                  )}
                  <p className="text-xs text-gray-400 mt-1">
                    {new Date(item.createdAt).toLocaleDateString()}
                  </p>
                </div>
              </div>
              <div className="px-4 py-3 bg-gray-50 dark:bg-gray-900/40 border-t border-gray-100 dark:border-gray-700 flex items-center justify-between">
                {item.url ? (
                  <a
                    href={item.url}
                    target="_blank"
                    rel="noreferrer"
                    className="flex items-center gap-1 text-xs text-blue-600 dark:text-blue-400 hover:text-blue-800 dark:hover:text-blue-300"
                  >
                    <ArrowTopRightOnSquareIcon className="h-4 w-4" />
                    Open
                  </a>
                ) : (
                  <span className="text-xs text-gray-400">No preview</span>
                )}
                <button
                  onClick={() => setShareItem(item)}
                  className="flex items-center gap-1 text-xs text-gray-700 dark:text-gray-300 hover:text-blue-600 dark:hover:text-blue-400"
                >
                  <ShareIcon className="h-4 w-4" />
                  Share to project
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {!loading && activeTab.id !== 'images' && items.length > 0 && (
        <Pagination
          currentPage={page}
          totalPages={Math.ceil(totalItems / ITEMS_PER_PAGE)}
          onPageChange={setPage}
          totalItems={totalItems}
          itemsPerPage={ITEMS_PER_PAGE}
        />
      )}

      {/* Share modal */}
      {shareItem && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl w-full max-w-md p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white">Share to Project</h3>
              <button onClick={() => setShareItem(null)} className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300">
                <XMarkIcon className="h-5 w-5" />
              </button>
            </div>
            <p className="text-sm text-gray-600 dark:text-gray-400 mb-4">
              Share <strong>{shareItem.name}</strong> to another project. This adds a reference to that project's "Shared Media" folder.
            </p>
            <form onSubmit={handleShare}>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Target project</label>
              <select
                value={selectedProjectId}
                onChange={(e) => setSelectedProjectId(e.target.value)}
                required
                className="w-full border border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-white rounded-md p-2 focus:ring-blue-500 focus:border-blue-500 mb-4"
              >
                <option value="">Select a project</option>
                {projects.map((project) => (
                  <option key={project._id} value={project._id}>
                    {project.name}
                  </option>
                ))}
              </select>
              <div className="flex justify-end gap-3">
                <button
                  type="button"
                  onClick={() => setShareItem(null)}
                  className="px-4 py-2 text-sm text-gray-700 dark:text-gray-300 hover:text-gray-900 dark:hover:text-white"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={!selectedProjectId || sharing}
                  className="px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-md hover:bg-blue-700 disabled:opacity-50"
                >
                  {sharing ? 'Sharing...' : 'Share'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default ProjectFilesPage;
